import React, { useContext } from 'react';
import { Alert, Box, Button, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { ColumnDataType, ColumnDef, ColumnSourceType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDef } from 'src/components/segment-cdt/create/segmentCreateUtils';
import GlPullCriteriaFields from 'src/components/segment-cdt/create/GlPullCriteriaFields';
import SegmentGlPullDataSource from 'src/components/segment-cdt/create/SegmentGlPullDataSource';

const isDerived = (c: ColumnDef) => c.sourceType === ColumnSourceType.DERIVED_PCT || c.sourceType === ColumnSourceType.DERIVED_BAL;

/**
 * Derived column config, used only by the detail-page Add Columns modal (a derived column references
 * other columns on the same table, so it needs an existing table). Handles both kinds, keyed off the
 * draft's sourceType chosen in the type chooser:
 *   DERIVED_PCT  a Percentage column = each segment's share of a NUMBER column's total for the period.
 *   DERIVED_BAL  a Number column = a PERCENTAGE column applied to this column's own aggregated GL pull.
 * Each takes exactly one reference, and the two want different dataTypes because their arithmetic differs -
 * one divides, one multiplies. References are offered from the table's existing columns plus the queued
 * additions, filtered to that dataType and never a derived column (single-hop rule). The DERIVED_BAL branch
 * reuses {@link SegmentGlPullDataSource} for its ledger + COA data keys. Reads everything from context.
 */
export default function DerivedColumnForm({ hideCancel = false }: { hideCancel?: boolean }) {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const draft = editor?.draft ?? {};
    const dc = draft.derivedConfig ?? {};
    const pc = draft.pullCriteria ?? {};
    const isBal = draft.sourceType === ColumnSourceType.DERIVED_BAL;
    // The refusal for this draft, held beside it in the reducer so SET_COLUMN_DRAFT drops it on any edit.
    const localError = editor?.error ?? '';

    // Only the DERIVED_BAL branch has a currency field, so skip the fetch for the % kind (an empty query
    // makes the underlying hook no-op).
    const [currencyOptions] = services.customDataTablesService.getCurrencyCodeLOV(isBal ? 'load' : '');

    // Reference candidates: the table's existing columns plus the columns queued in this session. Each kind
    // needs a different dataType, because the two derivations do different arithmetic with their one operand:
    // DERIVED_PCT divides (a segment's share of a NUMBER column's total), DERIVED_BAL multiplies (a PERCENTAGE
    // applied to the aggregate).
    const union: ColumnDef[] = [...(state.detail.definition?.columns ?? []), ...cw.columns];
    const nonDerived = union.filter((c) => !isDerived(c));
    const asOption = (c: ColumnDef) => ({ value: c.columnId ?? '', label: c.displayName || c.columnId || '' });
    // A % of total column IS a percentage, and it is the obvious basis to allocate a balance across, so the
    // allocation list is every PERCENTAGE column on the table — manual, derived, whatever produced it. The
    // single-hop rule still holds in the other direction: DERIVED_PCT's own operand stays non-derived, which
    // keeps the longest chain to two hops (a derived balance may read a derived percentage; nothing may read a
    // derived balance) and cycle-free.
    const percentageOptions = union.filter((c) => c.dataType === ColumnDataType.PERCENTAGE).map(asOption);
    const numberOptions = nonDerived.filter((c) => c.dataType === ColumnDataType.NUMBER).map(asOption);

    const setDraft = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload(patch));
    const setCriteria = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload({ pullCriteria: patch }));
    const setDerived = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload({ derivedConfig: patch }));

    const onAdd = () => {
        const others = union.map((c) => c.displayName || '');
        const otherIds = union.map((c) => c.columnId).filter((id): id is string => !!id);
        const validCurrencies = (currencyOptions ?? []).length ? [CONSTANTS.STAT_CURRENCY, ...(currencyOptions ?? [])] : [];
        const { column, error } = buildColumnDef(draft, others, otherIds, validCurrencies, union);
        if (error || !column) {
            dispatch(ACTIONS.SET_COLUMN_ERROR.withPayload(error || 'Invalid column.'));
            return;
        }
        dispatch(ACTIONS.COMMIT_COLUMN.withPayload(column));
    };

    return (
        <SpaceBetween size="m">
            <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>
                {isBal ? 'Derived balance (% applied to an aggregated GL pull)' : 'Derived: % of total'}
            </Box>
            <FormField label="Column name" description="Enter a unique name for this column.">
                <Input
                    value={draft.displayName ?? ''}
                    placeholder={isBal ? 'e.g. 82453 IC Balance' : 'e.g. 2D HC %'}
                    onChange={({ detail }) => setDraft({ displayName: detail.value })}
                />
            </FormField>

            {!isBal && (
                // One operand: this column's value for a segment is that segment's share of the source
                // column's period total, so the source column is both numerator and denominator and there is
                // nothing else to ask for. Full width rather than half — there is no second field to pair it
                // with — and the description states the arithmetic, since "source" alone does not say which
                // way round the division goes.
                <FormField
                    label="Source column"
                    description="A Number column on this table. Each segment's value becomes its share of that column's total for the period."
                >
                    <Select
                        selectedOption={numberOptions.find((o) => o.value === dc.sourceColumnId) ?? null}
                        options={numberOptions}
                        placeholder={numberOptions.length ? 'Select a source column' : 'No Number columns available'}
                        empty="No Number columns available"
                        onChange={({ detail }) => setDerived({ sourceColumnId: detail.selectedOption.value })}
                    />
                </FormField>
            )}

            {isBal && (
                <>
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                        <img src={CONSTANTS.IMAGE_PATH + 'segment-gl-pull.svg'} width={28} height={28} alt="" style={{ display: 'block' }} />
                        <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>Aggregated GL balance pull</Box>
                    </SpaceBetween>
                    {/* The same scalar settings a PULL_GL column offers, from one place: a derived balance's
                        aggregate IS a GL pull, so it takes an FX conversion on the same terms. */}
                    <GlPullCriteriaFields
                        criteria={pc}
                        currency={draft.currency}
                        onCriteriaChange={setCriteria}
                        onCurrencyChange={(currency) => setDraft({ currency })}
                    />

                    <SegmentGlPullDataSource value={pc} onChange={(patch) => setCriteria(patch)} />

                    {/* A Percentage column, not a Number one. The derivation multiplies: each segment's value is
                        `pct / 100 x aggregate` (`DerivationEngine.deriveBal`, whose parameter is documented as
                        "the column holding the whole-percent multiplier"), so the column has to already hold
                        percentages. Offering Number columns made the field unusable on a table whose percentages
                        live in a Percentage column, which is the reported bug.

                        Held as `pctColumnId`, which is the field the derivation actually reads for this kind —
                        `ValidateAndDeriveComponent` skips a DERIVED_BAL column that has no `pctColumnId`. Writing
                        it to `sourceColumnId` (which DERIVED_PCT uses) made the save fail outright, because that
                        field is required to reference a NUMBER column. */}
                    <FormField
                        label="Allocation % column"
                        description="A Percentage column on this table. Each segment gets that percentage of the aggregated balance."
                    >
                        <Select
                            selectedOption={percentageOptions.find((o) => o.value === dc.pctColumnId) ?? null}
                            options={percentageOptions}
                            placeholder={percentageOptions.length ? 'Select a percentage column' : 'No Percentage columns available'}
                            empty="No Percentage columns available"
                            onChange={({ detail }) => setDerived({ pctColumnId: detail.selectedOption.value })}
                        />
                    </FormField>
                </>
            )}

            {localError && <Alert type="error">{localError}</Alert>}

            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    {!hideCancel && <Button onClick={() => dispatch(ACTIONS.CANCEL_COLUMN_EDITOR)}>Cancel</Button>}
                    <Button variant="primary" onClick={onAdd}>Add column</Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
